import { CONFIG } from '@/config';
import { platform } from '@/platform';
import type { Plan, PlanMedia } from '@/types';
import {
  fetchPlan,
  sendHeartbeat,
  sendHealthReport,
  uploadProofOfPlay,
  uploadPresenceData,
  checkConnectivity,
} from './api-client';
import {
  getCachedPlan,
  setCachedPlan,
  getCachedPlanVersion,
  getProofOfPlayQueue,
  markProofOfPlaySynced,
  clearSyncedProofOfPlay,
  getPresenceQueue,
  markPresenceSynced,
  clearSyncedPresence,
  setLastSyncTime,
} from './offline-store';
import {
  downloadAllMedia,
  setProtectedMedia,
  evictMedia,
  getAllCachedIds,
  getCachedSha256,
} from '@/playback/media-cache';
import { collectHealth } from '@/health/device-reporter';
import { logger } from './logger';

type SyncCallback = (plan: Plan) => void;

let pollTimeout: ReturnType<typeof setTimeout> | null = null;
let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
let onSyncComplete: SyncCallback | null = null;

// Single-flight lock. The sync interval can fire while the previous run is
// still in progress (network slow, large media batch). Without a lock the
// second run dups proof-of-play uploads and races on cache writes.
let isSyncing = false;

/** Backoff state for failed plan fetches. Capped at 2× pollIntervalSec. */
let consecutiveFailures = 0;

const SOFTWARE_VERSION = '0.1.0';

export function setSyncCallback(cb: SyncCallback): void {
  onSyncComplete = cb;
}

/**
 * Compute the next-poll delay in milliseconds. Honors server-driven
 * pollIntervalSec, applies exponential backoff on consecutive errors
 * (capped at 2× the configured interval), and adds 0–30 s of jitter to
 * keep a fleet rebooting in unison from hammering the server in lock-step.
 */
function computeNextDelay(plan: Plan | null, hadError: boolean): number {
  const baseSec = plan?.pollIntervalSec ?? 300;
  let delaySec = baseSec;
  if (hadError) {
    delaySec = Math.min(baseSec * 2, baseSec * Math.pow(2, consecutiveFailures));
  }
  const jitterMs = Math.floor(Math.random() * 30_000);
  return delaySec * 1000 + jitterMs;
}

/**
 * Diff cached vs incoming media set.
 *  - toAdd: present in plan, absent in cache (or sha mismatch)
 *  - toRemove: in cache but not referenced by any plan item
 * "Changed" media (same id, different sha256) ends up in toAdd because
 * downloadMedia evicts the stale entry before re-fetching.
 */
function diffMedia(plan: Plan): { toAdd: PlanMedia[]; toRemove: string[] } {
  const planMedia = new Map<string, PlanMedia>();
  for (const item of plan.items) {
    for (const m of item.media) planMedia.set(m.id, m);
  }

  const cachedIds = getAllCachedIds();
  const toAdd: PlanMedia[] = [];
  for (const [id, m] of planMedia) {
    const cachedSha = getCachedSha256(id);
    if (cachedSha === undefined) {
      toAdd.push(m);
    } else if (m.sha256 && cachedSha !== m.sha256) {
      toAdd.push(m); // content rotated; downloadMedia will evict + redownload
    }
  }

  const toRemove: string[] = [];
  for (const id of cachedIds) {
    if (!planMedia.has(id)) toRemove.push(id);
  }

  return { toAdd, toRemove };
}

async function runSync(boardId: string): Promise<boolean> {
  if (isSyncing) {
    logger.debug('[Sync] Skipping — previous sync still running');
    return false;
  }
  isSyncing = true;
  let hadError = false;

  logger.info('[Sync] Starting sync cycle');

  const online = await checkConnectivity();
  if (!online) {
    logger.warn('[Sync] Offline — using cached plan; will retry');
    isSyncing = false;
    scheduleNextPoll(boardId, getCachedPlan(), true);
    return false;
  }

  let freshPlan: Plan | null = null;

  try {
    freshPlan = await fetchPlan();
    const cachedVersion = getCachedPlanVersion();
    const planChanged = freshPlan.planVersion !== cachedVersion;

    const { toAdd, toRemove } = diffMedia(freshPlan);

    if (!planChanged && toAdd.length === 0 && toRemove.length === 0) {
      logger.debug('[Sync] Plan unchanged + media cached — skipping', {
        version: freshPlan.planVersion,
      });
    } else {
      if (planChanged) {
        logger.info('[Sync] Plan changed', {
          from: cachedVersion,
          to: freshPlan.planVersion,
          items: freshPlan.items.length,
        });
      } else {
        logger.info('[Sync] Plan same but cache needs reconcile', {
          added: toAdd.length,
          removed: toRemove.length,
        });
      }

      for (const id of toRemove) {
        logger.debug('[Sync] Evicting media no longer in plan', { id });
        await evictMedia(id);
      }

      if (toAdd.length > 0) {
        logger.info('[Sync] Downloading media', { count: toAdd.length });
        await downloadAllMedia(toAdd);
      }

      setCachedPlan(freshPlan);

      // Tell the media cache which IDs are "in use" — eviction under
      // quota pressure will skip these in favour of older/unused
      // media. Set covers everything in the current plan; the cache
      // still LRU-evicts inside this set as a last resort.
      const protectedIds: string[] = [];
      for (const it of freshPlan.items) {
        for (const m of it.media) protectedIds.push(m.id);
      }
      setProtectedMedia(protectedIds);

      if (onSyncComplete) onSyncComplete(freshPlan);
    }

    const popQueue = getProofOfPlayQueue().filter((r) => !r.synced);
    if (popQueue.length > 0) {
      try {
        await uploadProofOfPlay(
          popQueue.map(({ id: _id, synced: _s, ...rest }) => rest),
        );
        markProofOfPlaySynced(popQueue.map((r) => r.id));
        clearSyncedProofOfPlay();
        logger.info('[Sync] Uploaded proof-of-play', { count: popQueue.length });
      } catch (err) {
        logger.warn('[Sync] Proof-of-play upload failed', { err: String(err) });
      }
    }

    const presQueue = getPresenceQueue().filter((r) => !r.synced);
    if (presQueue.length > 0) {
      try {
        await uploadPresenceData(
          presQueue.map(({ id: _id, synced: _s, ...rest }) => rest),
        );
        markPresenceSynced(presQueue.map((r) => r.id));
        clearSyncedPresence();
        logger.info('[Sync] Uploaded presence', { count: presQueue.length });
      } catch (err) {
        logger.warn('[Sync] Presence upload failed', { err: String(err) });
      }
    }

    try {
      const health = await collectHealth();
      await sendHealthReport(
        boardId,
        { ...health, softwareVersion: SOFTWARE_VERSION },
        platform.kind,
      );
    } catch (err) {
      logger.warn('[Sync] Health report failed', { err: String(err) });
    }

    setLastSyncTime(Date.now());
    consecutiveFailures = 0;
    logger.info('[Sync] Sync complete');
  } catch (err) {
    hadError = true;
    consecutiveFailures += 1;
    logger.error('[Sync] Sync failed', {
      err: String(err),
      stack: (err as Error)?.stack,
    });
  } finally {
    isSyncing = false;
  }

  scheduleNextPoll(boardId, freshPlan ?? getCachedPlan(), hadError);
  return !hadError;
}

/**
 * Single reschedule point. MUST be reached on every runSync exit path
 * (success, error, AND offline) or the poll loop dies. computeNextDelay
 * applies server-driven interval + jitter, or exponential backoff when
 * the last attempt failed.
 */
function scheduleNextPoll(boardId: string, plan: Plan | null, hadError: boolean): void {
  const delay = computeNextDelay(plan, hadError);
  if (pollTimeout) clearTimeout(pollTimeout);
  pollTimeout = setTimeout(() => runSync(boardId), delay);
  logger.debug('[Sync] Next poll scheduled', { delaySec: delay / 1000 });
}

export function startSync(boardId: string): void {
  logger.info('[Sync] Running first sync now');
  runSync(boardId);

  // Heartbeat (more frequent than full sync)
  heartbeatInterval = setInterval(async () => {
    try {
      await sendHeartbeat(boardId, SOFTWARE_VERSION, platform.kind);
    } catch {
      // Ignore heartbeat failures
    }
  }, CONFIG.HEARTBEAT_INTERVAL_MS);

  logger.info('[Sync] Sync manager started');
}

export function stopSync(): void {
  if (pollTimeout) {
    clearTimeout(pollTimeout);
    pollTimeout = null;
  }
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}
